import { ChangeDetectionStrategy, Component, inject, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { PowerProfile } from '@app/shared/models/power-profile.interface';
import { ConfirmationDialogService } from '@services/confirmation-dialog/confirmation-dialog.service';
import { ConfirmDialogData } from '@components/dialogs/confirm/confirm.component';

export type PowerProfileDialogMode = 'create' | 'edit';

export type PowerProfileDialogData = {
  buildingId: number;
  mode: PowerProfileDialogMode;
  profile?: PowerProfile;
};

export type PowerProfileDialogResult = {
  name: string;
  maximumWattage: number | null;
  standbyWattage: number | null;
  baseWattage: number | null;
};

@Component({
  selector: 'app-power-profile-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './power-profile-dialog.component.html',
  styleUrl: './power-profile-dialog.component.scss'
})
export class PowerProfileDialogComponent {
  form: FormGroup;
  private confirmationDialogService = inject(ConfirmationDialogService);

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: PowerProfileDialogData,
    private dialogRef: MatDialogRef<PowerProfileDialogComponent>
  ) {
    this.form = this.fb.group({
      name: [data.profile?.name ?? '', [Validators.required]],
      maximumWattage: [data.profile?.maximumWattage ?? null, [Validators.required, Validators.min(0)]],
      standbyWattage: [data.profile?.standbyWattage ?? null, [Validators.min(0)]],
      baseWattage: [data.profile?.baseWattage ?? null, [Validators.min(0)]]
    });
  }

  get title(): string {
    return this.data.mode === 'edit' ? 'Edit Power Profile' : 'Create Power Profile';
  }

  get submitLabel(): string {
    return this.data.mode === 'edit' ? 'Save' : 'Create';
  }

  cancel(): void {
    this.dialogRef.close();
  }

  save(): void {
    if (this.form.invalid) return;

    const value = this.form.value as PowerProfileDialogResult;
    const { maximumWattage, standbyWattage, baseWattage } = value;

    const isStandbyHigher = maximumWattage != null && standbyWattage != null && standbyWattage > maximumWattage;
    const isBaseHigher = maximumWattage != null && baseWattage != null && baseWattage > maximumWattage;

    if (isStandbyHigher || isBaseHigher) {
      const message =
        'One or more power values are higher than the maximum power. Please check that the values are correct before saving.';
      const dialogData = new ConfirmDialogData(message, 'Are these values correct?', 'Save anyway', 'Go back');
      this.confirmationDialogService.open(dialogData).subscribe((confirmed) => {
        if (confirmed) {
          this.dialogRef.close(value);
        }
      });
      return;
    }

    this.dialogRef.close(value);
  }
}
